import * as React from "react";
import { Box } from "@mui/material";
import type { KitchenArrivalRow, ShotNumberOverlay } from "./RallyBoardPopup";
import type { RenderPlayerIdentity, Team01 } from "./GameStoryDialog_UPDATED";
import { TIME_LINE_FAST_SHOT_MPH } from "./ActiveRallyVideo";

type PlayerId = 0 | 1 | 2 | 3;

type StripPoint = {
  shotNumber: number;
  atMsRel: number;
  qualityOverall: number;
  qualityExecution: number | null;
  qualitySelection: number | null;
  playerId: PlayerId;
  isFastShot: boolean;
  speedMph: number | null;
};

type RawShotQuality = {
  overall?: unknown;
  execution?: unknown;
  selection?: unknown;
};

function teamOfPlayer(playerId: PlayerId): Team01 {
  return playerId === 0 || playerId === 1 ? 0 : 1;
}

function teamColor(team: Team01, alpha = 1): string {
  return team === 0
    ? `rgba(95,165,255,${alpha})`
    : `rgba(120,225,160,${alpha})`;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function numOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function isPlayerId(v: unknown): v is PlayerId {
  return v === 0 || v === 1 || v === 2 || v === 3;
}

function getOverlayShotQualityParts(overlay: ShotNumberOverlay): {
  overall: number | null;
  execution: number | null;
  selection: number | null;
} {
  const q = overlay.rawShot?.quality as RawShotQuality | undefined;

  return {
    overall: numOrNull(q?.overall),
    execution: numOrNull(q?.execution),
    selection: numOrNull(q?.selection),
  };
}

function buildStripPoints(row: KitchenArrivalRow | null): StripPoint[] {
  if (!row) return [];

  const overlays = [
    ...(Array.isArray(row.shotNumberOverlays) ? row.shotNumberOverlays : []),
  ].sort((a, b) => (a.rawShot?.start_ms ?? 0) - (b.rawShot?.start_ms ?? 0));

  const out: StripPoint[] = [];

  for (let i = 0; i < overlays.length; i++) {
    const overlay = overlays[i];
    const rawShot = overlay.rawShot;
    const playerIdValue = overlay.playerId ?? rawShot?.player_id;
    const playerIdNum = Number(playerIdValue);

    if (!isPlayerId(playerIdNum)) continue;

    const q = getOverlayShotQualityParts(overlay);
    if (q.overall == null) continue;

    const speedMph =
      typeof rawShot?.resulting_ball_movement?.speed === "number"
        ? rawShot.resulting_ball_movement.speed
        : null;

    const isFastShot = speedMph != null && speedMph >= TIME_LINE_FAST_SHOT_MPH;

    out.push({
      shotNumber: i + 1,
      qualityOverall: q.overall,
      qualityExecution: q.execution,
      qualitySelection: q.selection,
      playerId: playerIdNum,
      isFastShot,
      speedMph,
      atMsRel:
        typeof overlay.atMsRel === "number"
          ? overlay.atMsRel
          : typeof rawShot?.start_ms === "number"
            ? rawShot.start_ms
            : i * 1000,
    });
  }

  return out;
}

export function RallyShotQualityStrip(props: {
  row: KitchenArrivalRow | null;
  renderPlayerIdentity?: RenderPlayerIdentity;
  currentShotNumber?: number | null;
  focusShotNumber?: number | null;
  selectedPlayerId?: PlayerId | null;
  height?: number;
}) {
  const {
    row,
    renderPlayerIdentity,
    currentShotNumber = null,
    focusShotNumber = null,
    selectedPlayerId = null,
    height = 78,
  } = props;

  const [recentFastShotBursts, setRecentFastShotBursts] = React.useState<
    Array<{ shotNumber: number; startedAt: number }>
  >([]);

  const points = React.useMemo(() => buildStripPoints(row), [row]);

  const width = 900;
  const padLeft = 20;
  const padRight = 34;
  const padTop = 15;
  const padBottom = 17;

  const innerW = width - padLeft - padRight;
  const innerH = height - padTop - padBottom;

  const prevCurrentShotNumberRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    prevCurrentShotNumberRef.current = currentShotNumber;
  }, [currentShotNumber]);

  React.useEffect(() => {
    if (currentShotNumber == null) return;

    const currentPoint = points.find((p) => p.shotNumber === currentShotNumber);
    if (!currentPoint?.isFastShot) return;

    const now = performance.now();

    setRecentFastShotBursts((prev) => {
      const filtered = prev.filter((b) => now - b.startedAt < 900);

      if (filtered.some((b) => b.shotNumber === currentShotNumber)) {
        return filtered;
      }

      return [...filtered, { shotNumber: currentShotNumber, startedAt: now }];
    });
  }, [currentShotNumber, points]);

  React.useEffect(() => {
    if (!recentFastShotBursts.length) return;

    const timer = window.setInterval(() => {
      const now = performance.now();
      setRecentFastShotBursts((prev) =>
        prev.filter((b) => now - b.startedAt < 900),
      );
    }, 60);

    return () => window.clearInterval(timer);
  }, [recentFastShotBursts.length]);

  if (!points.length) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          color: "rgba(255,255,255,0.44)",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          bgcolor: "rgba(255,255,255,0.015)",
        }}
      >
        No shot quality data
      </Box>
    );
  }

  const minMs = Math.min(...points.map((p) => p.atMsRel));
  const maxMs = Math.max(...points.map((p) => p.atMsRel));

  const xForMs = (ms: number) =>
    padLeft + ((ms - minMs) / Math.max(1, maxMs - minMs)) * innerW;

  const yFor = (quality: number) =>
    padTop + (1 - clamp(quality, 0, 1)) * innerH;

  const plotted = points.map((p) => ({
    ...p,
    x: xForMs(p.atMsRel),
    y: yFor(p.qualityOverall),
  }));

  const pathD = plotted
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");

  const winningTeam =
    typeof row?.winnerTeam === "number" ? (row.winnerTeam as Team01) : null;

  const servingTeam =
    row?.servingTeam === 0 || row?.servingTeam === 1 ? row.servingTeam : null;

  const markerSize = 25; // 30; // 22;
  const markerHalf = markerSize / 2;

  const FAST_SHOT_BURST_VECTORS = [
    { dx: 0, dy: -16, angle: 0 },
    { dx: 11, dy: -11, angle: 45 },
    { dx: 16, dy: 0, angle: 90 },
    { dx: 11, dy: 11, angle: 135 },
    { dx: 0, dy: 16, angle: 180 },
    { dx: -11, dy: 11, angle: 225 },
    { dx: -16, dy: 0, angle: 270 },
    { dx: -11, dy: -11, angle: 315 },
  ];

  return (
    <Box
      sx={{
        px: 0.9,
        pt: 0, //  0.45,
        pb: 0.38,
        borderTop: "1px solid rgba(255,255,255,0.07)",
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        bgcolor: "rgba(255,255,255,0.018)",
        display: "flex",
        alignItems: "stretch",
        gap: 0.45,
      }}
    >
      <Box
        sx={{
          width: 16,
          minWidth: 16,
          position: "relative",
          userSelect: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            bottom: 0, // `${padTop + innerH * 0.5 * 0}px`,
            transform: "translateY(-50%)",
            fontSize: 10,
            fontWeight: 700,
            color: "rgba(0,0,0,1)",
            letterSpacing: 0.2,
          }}
        >
          Shot Quality
        </Box>
      </Box>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ display: "block", width: "100%", height, overflow: "visible" }}
      >
        <defs>
          <style>
            {`
      @keyframes rallyFastShotBurstCore {
        0% {
          opacity: 0.95;
          transform: scale(0.35);
        }
        45% {
          opacity: 0.78;
          transform: scale(1.0);
        }
        100% {
          opacity: 0;
          transform: scale(1.65);
        }
      }

      @keyframes rallyFastShotBurstRay {
        0% {
          opacity: 0.98;
          transform: scaleY(0.25) scaleX(0.8);
        }
        100% {
          opacity: 0;
          transform: scaleY(1.0) scaleX(1.0);
        }
      }

      @keyframes rallyFastShotSpark {
        0% {
          opacity: 1;
          transform: translate(0px, 0px) scale(0.45);
        }
        100% {
          opacity: 0;
          transform: translate(var(--dx), var(--dy)) scale(1.0);
        }
      }
    `}
          </style>
        </defs>

        <path
          d={pathD}
          fill="none"
          stroke="rgba(0,0,0,0.26)"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {plotted.map((p, idx) => {
          const burst = recentFastShotBursts.find(
            (b) => b.shotNumber === p.shotNumber,
          );
          const burstAgeMs = burst ? performance.now() - burst.startedAt : null;
          const burstT =
            burstAgeMs == null
              ? null
              : Math.max(0, Math.min(1, burstAgeMs / 900));
          const showLingeringBurst = burstT != null && burstT < 1;

          const isSelectedPlayer =
            selectedPlayerId != null && p.playerId === selectedPlayerId;

          const isCurrent =
            currentShotNumber != null && p.shotNumber === currentShotNumber;

          const wasCurrent = prevCurrentShotNumberRef.current === p.shotNumber;
          const didJustBecomeCurrent = isCurrent && !wasCurrent;

          const isFocus =
            focusShotNumber != null && p.shotNumber === focusShotNumber;

          const isFirst = idx === 0;
          const isLast = idx === plotted.length - 1;

          const hitterTeam = teamOfPlayer(p.playerId);
          const isWinningLast =
            isLast && winningTeam != null && hitterTeam === winningTeam;

          const isLastShot = idx === plotted.length - 1;

          return (
            <g
              key={`${p.shotNumber}_${p.playerId}_${idx}`}
              transform={`translate(${p.x}, ${p.y})`}
            >
              {isSelectedPlayer && !isCurrent && (
                <rect
                  x={-markerHalf - 3}
                  y={-markerHalf - 3}
                  width={markerSize + 6}
                  height={markerSize + 6}
                  rx={7}
                  ry={7}
                  fill="none"
                  stroke={teamColor(hitterTeam, 0.96)} // "rgba(255,255,255,0.34)"
                  strokeWidth="1.6"
                  strokeDasharray="4 2"
                />
              )}

              {isFirst && servingTeam != null && (
                <circle
                  r={markerHalf + 3.2}
                  fill="none"
                  stroke={teamColor(servingTeam, 0.96)}
                  strokeWidth="2.1"
                />
              )}

              {isLastShot && (
                <circle
                  r={markerHalf + 3.2}
                  fill="none"
                  stroke={teamColor(hitterTeam, 0.96)}
                  strokeWidth="2.1"
                />
              )}

              {isFocus && !isCurrent && (
                <circle
                  r={markerHalf + 3}
                  fill="none"
                  stroke="rgba(255,235,120,0.52)"
                  strokeWidth="1.4"
                  strokeDasharray="3 2"
                />
              )}

              {isCurrent && (
                <circle
                  r={markerHalf + 4}
                  fill="none"
                  stroke={teamColor(hitterTeam, 0.96)}
                  strokeWidth="3.8"
                />
              )}

              <foreignObject
                x={-markerHalf}
                y={-markerHalf}
                width={markerSize}
                height={markerSize}
              >
                <Box
                  // sx={{
                  //   width: markerSize,
                  //   height: markerSize,
                  //   borderRadius: "4px",
                  //   overflow: "hidden",
                  //   bgcolor: "rgba(0,0,0,0.55)",
                  //   border: `1px solid ${teamColor(hitterTeam, 0.45)}`,
                  //   display: "flex",
                  //   alignItems: "center",
                  //   justifyContent: "center",
                  // }}
                  sx={{
                    width: markerSize,
                    height: markerSize,
                    overflow: "hidden",
                    display: "block",
                    borderRadius: "4px",
                  }}
                >
                  {renderPlayerIdentity ? (
                    renderPlayerIdentity({
                      playerId: p.playerId,
                      size: markerSize,
                      fontSize: 8,
                      noLabel: true,
                      borderWidth: "0px",
                      borderRadius: 0,
                      avatarOnly: true,
                    })
                  ) : (
                    <Box
                      sx={{
                        fontSize: 9,
                        fontWeight: 800,
                        color: "white",
                      }}
                    >
                      p{p.playerId}
                    </Box>
                  )}
                </Box>
              </foreignObject>

              {p.isFastShot && (
                <g
                  transform={`translate(${markerHalf + 2.6}, ${-markerHalf - 5})`}
                >
                  <text
                    x={0}
                    y={0.4}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="16"
                    style={{
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                    opacity={isCurrent ? 1 : 0.9}
                  >
                    ⚡
                  </text>
                </g>
              )}
              {p.isFastShot && showLingeringBurst && (
                <>
                  {(() => {
                    const t = burstT ?? 0;
                    const burstAlpha = 1 - t;
                    const coreR = 4.2 + t * 3.8;
                    const rayLen = 11 + t * 13;
                    const sparkDist = 7 + t * 19;
                    return (
                      <>
                        <circle
                          r={coreR}
                          cx={0}
                          cy={0}
                          fill={`rgba(255,205,120,${0.42 * burstAlpha})`}
                          stroke={`rgba(255,165,60,${0.88 * burstAlpha})`}
                          strokeWidth={1.8}
                          pointerEvents="none"
                        />

                        {FAST_SHOT_BURST_VECTORS.map((v, i) => {
                          const lenScale = i % 2 === 0 ? 1 : 0.82;
                          const x2 = (v.dx / 16) * rayLen * lenScale;
                          const y2 = (v.dy / 16) * rayLen * lenScale;

                          return (
                            <line
                              key={`ray_${i}`}
                              x1={0}
                              y1={0}
                              x2={x2}
                              y2={y2}
                              stroke={
                                i % 2 === 0
                                  ? `rgba(255,165,60,${0.92 * burstAlpha})`
                                  : `rgba(255,225,150,${0.82 * burstAlpha})`
                              }
                              strokeWidth={i % 2 === 0 ? 2.4 : 1.7}
                              strokeLinecap="round"
                              pointerEvents="none"
                            />
                          );
                        })}

                        {FAST_SHOT_BURST_VECTORS.map((v, i) => {
                          const x =
                            (v.dx / 16) *
                            sparkDist *
                            (i % 2 === 0 ? 1.0 : 0.88);
                          const y =
                            (v.dy / 16) *
                            sparkDist *
                            (i % 2 === 0 ? 1.0 : 0.88);

                          return (
                            <circle
                              key={`spark_${i}`}
                              r={
                                (i % 2 === 0 ? 2.7 : 2.0) *
                                (0.76 + 0.24 * burstAlpha)
                              }
                              cx={x}
                              cy={y}
                              fill={
                                i % 2 === 0
                                  ? `rgba(255,165,60,${0.98 * burstAlpha})`
                                  : `rgba(255,225,150,${0.92 * burstAlpha})`
                              }
                              pointerEvents="none"
                            />
                          );
                        })}
                      </>
                    );
                  })()}
                </>
              )}
              {/* {isLastShot && (
                <g transform={`translate(0, ${-(markerHalf + 10)})`}>
                  <rect
                    x={-8}
                    y={-6}
                    width={16}
                    height={12}
                    rx={5}
                    ry={5}
                    fill={
                      winningTeam != null && hitterTeam === winningTeam
                        ? "rgba(35,140,70,0.96)"
                        : "rgba(185,65,65,0.96)"
                    }
                    stroke="rgba(255,255,255,0.82)"
                    strokeWidth="0.8"
                  />
                  <text
                    x={0}
                    y={0.5}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="8.5"
                    fontWeight="800"
                    fill="white"
                    style={{ pointerEvents: "none", userSelect: "none" }}
                  >
                    {winningTeam != null && hitterTeam === winningTeam
                      ? "W"
                      : "L"}
                  </text>
                </g>
              )} */}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}
